import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink } from "lucide-react";
import { api, formatApiError, formatDateShort } from "@/lib/api";
import { SOURCE_LEVELS } from "@/lib/constants";
import PageHeading from "@/components/PageHeading";

export default function Trend() {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [trends, setTrends] = useState([]);
  const [adding, setAdding] = useState(false);
  const [nf, setNf] = useState({ title: "", text: "", url: "", source_level: "ufficiale" });

  useEffect(() => {
    api.get("/brands").then(({ data }) => {
      setBrands(data);
      if (data.length) setBrandId(data[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!brandId) return;
    api.get(`/brands/${brandId}/trends`).then(({ data }) => setTrends(data));
  }, [brandId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!nf.title.trim()) {
      toast.error("Titolo obbligatorio");
      return;
    }
    try {
      await api.post(`/brands/${brandId}/trends`, nf);
      setNf({ title: "", text: "", url: "", source_level: "ufficiale" });
      setAdding(false);
      toast.success("Voce salvata");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Eliminare "${t.title}"?`)) return;
    await api.delete(`/trends/${t.id}`);
    load();
  };

  const brand = brands.find((b) => b.id === brandId);

  return (
    <div data-testid="trend-page">
      <PageHeading kicker="07 · RICERCA" title="Ricerca Trend"
        subtitle="Note e ricerche su tendenze di marketing, organizzate per brand, mai in un elenco unico." />

      <div className="flex flex-wrap gap-2 mb-10">
        {brands.map((b) => (
          <button key={b.id} onClick={() => setBrandId(b.id)} data-testid={`trend-brand-${b.id}`}
            className={`px-5 py-2 text-[0.65rem] uppercase tracking-widest border transition-colors ${brandId === b.id ? "bg-black text-greige border-black" : "border-black/30 text-stone2e hover:border-black"}`}>
            {b.name}
          </button>
        ))}
      </div>

      {brand && (
        <>
          <p className="meta-label mb-5">SEZIONE · {brand.name.toUpperCase()}</p>
          <div className="mb-8">
            {adding ? (
              <div className="bg-white/70 border border-white p-5 max-w-xl">
                <input autoFocus value={nf.title} onChange={(e) => setNf({ ...nf, title: e.target.value })} placeholder="Titolo della tendenza *" data-testid="trend-title-input" className="field-line mb-3" />
                <textarea rows={3} value={nf.text} onChange={(e) => setNf({ ...nf, text: e.target.value })} placeholder="Note / risultati di ricerca" data-testid="trend-text-input" className="field-line resize-none mb-3" />
                <input value={nf.url} onChange={(e) => setNf({ ...nf, url: e.target.value })} placeholder="URL fonte (opz.)" data-testid="trend-url-input" className="field-line mb-4" />
                <p className="meta-label mb-2">GRADO DI VERIFICA DELLA FONTE</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {SOURCE_LEVELS.map((s) => (
                    <button key={s.key} onClick={() => setNf({ ...nf, source_level: s.key })} data-testid={`source-level-${s.key}`}
                      className={`px-4 py-1.5 text-[0.62rem] uppercase tracking-widest transition-colors ${nf.source_level === s.key ? s.cls : "border border-black/30 text-stone2e hover:border-black"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-3">
                  <button onClick={create} data-testid="save-trend-button" className="bg-black text-greige px-6 py-2 text-xs uppercase tracking-widest">Salva</button>
                  <button onClick={() => setAdding(false)} className="border border-black px-6 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">Annulla</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAdding(true)} data-testid="add-trend-button" className="inline-flex items-center gap-2 bg-black text-greige px-6 py-3 uppercase tracking-widest text-xs hover:bg-stone2e transition-colors">
                <Plus strokeWidth={1.5} size={16} /> Nuova voce
              </button>
            )}
          </div>

          {trends.length === 0 ? (
            <p className="font-editorial text-xl text-stone2e italic py-10 text-center">Nessuna ricerca salvata per questo brand.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {trends.map((t) => {
                const lvl = SOURCE_LEVELS.find((s) => s.key === t.source_level) || SOURCE_LEVELS[2];
                return (
                  <div key={t.id} className="group bg-white/60 border border-white p-5" data-testid={`trend-card-${t.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <span className={`px-2.5 py-0.5 text-[0.58rem] uppercase tracking-widest ${lvl.cls}`}>{lvl.label}</span>
                      <button onClick={() => remove(t)} data-testid={`delete-trend-${t.id}`} className="text-stone2e hover:text-ink opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} strokeWidth={1.5} /></button>
                    </div>
                    <h4 className="font-display text-xl text-ink mt-3">{t.title}</h4>
                    {t.text && <p className="font-body text-sm text-stone2e mt-1.5 whitespace-pre-wrap">{t.text}</p>}
                    <div className="flex items-center justify-between mt-4">
                      <span className="meta-label">{formatDateShort(t.created_at).toUpperCase()}</span>
                      {t.url && (
                        <a href={t.url} target="_blank" rel="noreferrer" data-testid={`trend-link-${t.id}`} className="inline-flex items-center gap-1 text-xs text-ink link-underline">
                          Fonte <ExternalLink size={11} strokeWidth={1.5} />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
